/**
 * Measure the width of text as it would be rendered
 */
function measureText(text: string, font: string): number {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (!context) return 0;
    context.font = font;
    return context.measureText(text).width;
}

export interface TruncationResult {
    displayText: string;
    wasTruncated: boolean;
}
/**
 * Determine the text + truncation text that fits within maxWidth
 */
export function getTruncatedText(stringArray: string[], maxWidth: number, font: string): TruncationResult {
    if (!stringArray.length) {
        return { displayText: "", wasTruncated: false };
    }

    const separator = ", ";
    const ellipsis = ", ...";
    const getMoreText = (count: number) => ` +${count} more`;

    // Pre-calculate common measurements
    const separatorWidth = measureText(separator, font);
    const ellipsisWidth = measureText(ellipsis, font);
    const getMoreTextWidth = (count: number) => measureText(getMoreText(count), font);

    const visibleZones: string[] = [];
    let currentWidth = 0;

    // Try adding zones one by one
    for (let i = 0; i < stringArray.length; i++) {
        const zoneName = stringArray[i];
        const zoneWidth = measureText(zoneName, font);
        const isLastZone = i === stringArray.length - 1;

        const newWidth = currentWidth + (visibleZones.length > 0 ? separatorWidth : 0) + zoneWidth;

        const remainingZones = stringArray.length - i - 1;
        const moreTextWidth = remainingZones > 0 ? getMoreTextWidth(remainingZones) : 0;
        const wouldOverflow = newWidth + ellipsisWidth + moreTextWidth > maxWidth;

        if (wouldOverflow && !isLastZone) {
            break;
        }

        visibleZones.push(zoneName);
        currentWidth = newWidth;
    }

    // If all zones fit, return them all
    if (visibleZones.length === stringArray.length) {
        return {
            displayText: visibleZones.join(separator),
            wasTruncated: false,
        };
    }

    // Remove zones from the end until it fits with ellipsis and "+N more"
    while (visibleZones.length > 0) {
        const remainingCount = stringArray.length - visibleZones.length;
        const text = visibleZones.join(separator);
        const moreText = getMoreText(remainingCount);

        if (measureText(text + ellipsis + moreText, font) <= maxWidth) {
            return {
                displayText: text + ellipsis + moreText,
                wasTruncated: true,
            };
        }

        visibleZones.pop();
    }

    // Fallback if nothing fits
    return {
        displayText: ellipsis + getMoreText(stringArray.length),
        wasTruncated: true,
    };
}
